import { Controller } from "@hotwired/stimulus"

const MODE_EXPANDED = "expanded"
const MODE_COMPACT = "compact"
const MODE_CLASS_EXPANDED = "educator-home-ai-assistant-modal--expanded"
const MODE_CLASS_COMPACT = "educator-home-ai-assistant-modal--compact"
const BODY_SCROLL_LOCK_CLASS = "modal-active"
const SCROLLBAR_COMPENSATION_VAR = "--railsui-modal-scrollbar-compensation"
const MODE_STORAGE_KEY = "educator-home-ai-assistant-mode"

const scrollbarWidth = () =>
  Math.max(0, window.innerWidth - document.documentElement.clientWidth)

const lockBodyScrollWithCompensation = () => {
  // Already locked (e.g. railsui-modal#show just ran) — keep its padding; re-measure
  // would read 0 after overflow:hidden and wipe compensation.
  if (document.body.classList.contains(BODY_SCROLL_LOCK_CLASS)) {
    return
  }

  const width = scrollbarWidth()
  document.body.classList.add(BODY_SCROLL_LOCK_CLASS)
  if (width > 0) {
    const value = `${width}px`
    document.body.style.paddingRight = value
    document.documentElement.style.setProperty(SCROLLBAR_COMPENSATION_VAR, value)
  } else {
    document.body.style.removeProperty("padding-right")
    document.documentElement.style.removeProperty(SCROLLBAR_COMPENSATION_VAR)
  }
}

const unlockBodyScrollCompensation = () => {
  document.body.classList.remove(BODY_SCROLL_LOCK_CLASS)
  document.body.style.removeProperty("padding-right")
  document.documentElement.style.removeProperty(SCROLLBAR_COMPENSATION_VAR)
}

// Opens/closes the home AI modal (railsui-modal) from header / Ask AI CTAs.
// Portals the modal to document.body so position:fixed covers the full viewport
// (DS layout nests page content inside a scrolling <main>).
// Expanded ↔ compact is a layout mode on the portaled modal (shared chat DOM).
export default class extends Controller {
  static targets = ["modal"]
  static values = {
    mode: { type: String, default: MODE_EXPANDED },
  }

  connect() {
    if (!this.hasModalTarget) return

    // Cache before portal — Stimulus targets must stay under the controller element.
    this.modalElement = this.modalTarget
    this.onChromeClick = this.onChromeClick.bind(this)
    this.onContainerClassMutation = this.onContainerClassMutation.bind(this)

    this.portalModal()
    this.modalElement.addEventListener("click", this.onChromeClick)
    this.observeContainerClosed()
    this.applyMode(MODE_EXPANDED)

    // Portaled open state must not survive Turbo cache / bfcache Back.
    this.navigationAbort?.abort()
    this.navigationAbort = new AbortController()
    const { signal } = this.navigationAbort
    document.addEventListener("turbo:before-cache", () => this.resetNavigationState(), { signal })
    document.addEventListener("turbo:before-visit", () => this.resetNavigationState(), { signal })
    window.addEventListener("pageshow", (event) => this.onPageShow(event), { signal })
  }

  disconnect() {
    this.navigationAbort?.abort()
    this.navigationAbort = null

    if (this.modalElement) {
      this.modalElement.removeEventListener("click", this.onChromeClick)
    }
    this.closeObserver?.disconnect()
    this.closeObserver = null
    this.restoreModal()
    this.modalElement = null
  }

  onPageShow(event) {
    if (!event.persisted) return
    this.resetNavigationState({ reportal: true })
  }

  resetNavigationState({ reportal = false } = {}) {
    if (!this.modalElement) return

    this.modalController?.close?.()

    const container = this.modalElement.querySelector("[data-railsui-modal-target='container']")
    if (container) {
      container.classList.add("hidden")
      container.setAttribute("aria-hidden", "true")
    }

    unlockBodyScrollCompensation()
    this.applyMode(MODE_EXPANDED)
    this.syncCompactBodyScrollSkip(MODE_EXPANDED)
    this.setClickOutside(true)
    this.restoreModal()
    if (reportal) this.portalModal()
  }

  open(event) {
    event?.preventDefault?.()
    const modal = this.modalController
    if (!modal) return

    if (typeof modal.show === "function") {
      modal.show(event)
    } else {
      modal.open(event)
    }

    this.enterMode(this.readStoredMode())
    // ai-messages-scroll connects while the dialog is still hidden (scrollHeight ≈ 0).
    this.scrollMessagesToLatest()
  }

  close(event) {
    event?.preventDefault?.()
    this.modalController?.close(event)
    // Idle DOM back to expanded chrome; preference stays in localStorage for next open.
    this.applyMode(MODE_EXPANDED)
    this.syncCompactBodyScrollSkip(MODE_EXPANDED)
    this.setClickOutside(true)
  }

  collapse(event) {
    event?.preventDefault?.()
    if (!this.modalElement) return

    this.persistMode(MODE_COMPACT)
    this.enterMode(MODE_COMPACT)
    this.scrollMessagesToLatest()
  }

  expand(event) {
    event?.preventDefault?.()
    if (!this.modalElement) return

    this.persistMode(MODE_EXPANDED)
    this.enterMode(MODE_EXPANDED)
    // Trap was cleared on collapse; re-arm. Do not do this on open — show() already traps,
    // and a second lockBackground leaves the page inert after one close.
    this.modalController?.scheduleFocusTrap?.()
    this.scrollMessagesToLatest()
  }

  onChromeClick(event) {
    const collapseBtn = event.target.closest?.("[data-educator-home-ai-collapse]")
    if (collapseBtn) {
      this.collapse(event)
      return
    }

    const expandBtn = event.target.closest?.("[data-educator-home-ai-expand]")
    if (expandBtn) {
      this.expand(event)
      return
    }

    const selectionsBtn = event.target.closest?.("[data-educator-home-ai-open-selected-items]")
    if (selectionsBtn) {
      this.openSelectedItems(event)
    }
  }

  openSelectedItems(event) {
    event?.preventDefault?.()
    const trigger = this.modalElement?.querySelector(
      ".educator-home-ai-selected-items-modal-host [data-action*='railsui-modal#open']"
    )
    trigger?.click()
  }

  observeContainerClosed() {
    const container = this.modalElement?.querySelector("[data-railsui-modal-target='container']")
    if (!container) return

    this.closeObserver = new MutationObserver(this.onContainerClassMutation)
    this.closeObserver.observe(container, { attributes: true, attributeFilter: ["class"] })
  }

  onContainerClassMutation() {
    const container = this.modalElement?.querySelector("[data-railsui-modal-target='container']")
    if (!container || !container.classList.contains("hidden")) return

    // Preferenced mode is kept in localStorage; reset idle chrome only.
    this.applyMode(MODE_EXPANDED)
    this.syncCompactBodyScrollSkip(MODE_EXPANDED)
    this.setClickOutside(true)
  }

  enterMode(mode) {
    this.applyMode(mode)
    this.syncCompactBodyScrollSkip(mode)

    if (mode === MODE_COMPACT) {
      // Page must scroll in compact — unlock and drop padding as the scrollbar returns.
      unlockBodyScrollCompensation()
      this.setClickOutside(false)
      this.modalController?.deactivateFocusTrap?.()
      return
    }

    // Expanded: hide page scrollbar but keep width via padding-right compensation.
    // Do not scheduleFocusTrap here — railsui-modal#show already did. A second
    // lockBackground/lockBodyScroll leaks inert and makes the whole page unclickable.
    lockBodyScrollWithCompensation()
    this.setClickOutside(true)
  }

  syncCompactBodyScrollSkip(mode) {
    const container = this.modalElement?.querySelector("[data-railsui-modal-target='container']")
    if (!container) return

    // So closing nested feedback does not leave modal-active (compact container stays open).
    if (mode === MODE_COMPACT) {
      container.setAttribute("data-railsui-modal-skip-body-scroll-lock", "true")
    } else {
      container.removeAttribute("data-railsui-modal-skip-body-scroll-lock")
    }
  }

  applyMode(mode) {
    this.modeValue = mode
    if (!this.modalElement) return

    this.modalElement.classList.toggle(MODE_CLASS_EXPANDED, mode === MODE_EXPANDED)
    this.modalElement.classList.toggle(MODE_CLASS_COMPACT, mode === MODE_COMPACT)
    this.modalElement.dataset.educatorHomeAiAssistantMode = mode
  }

  readStoredMode() {
    try {
      return localStorage.getItem(MODE_STORAGE_KEY) === MODE_COMPACT ? MODE_COMPACT : MODE_EXPANDED
    } catch {
      return MODE_EXPANDED
    }
  }

  persistMode(mode) {
    try {
      localStorage.setItem(MODE_STORAGE_KEY, mode)
    } catch {
      // Ignore quota / private-mode failures.
    }
  }

  setClickOutside(enabled) {
    const modal = this.modalController
    if (!modal || modal.clickOutsideValue === undefined) return
    modal.clickOutsideValue = enabled
  }

  scrollMessagesToLatest() {
    const container = this.modalElement?.querySelector(".chatbot-messages")
    if (!container) return

    const scroll = () => {
      container.scrollTop = container.scrollHeight
    }

    requestAnimationFrame(() => {
      scroll()
      requestAnimationFrame(scroll)
    })
  }

  portalModal() {
    if (!this.modalElement || this.modalElement.dataset.portaled === "true") return

    this.modalHome = document.createComment("educator-home-ai-assistant-modal-home")
    this.modalElement.before(this.modalHome)
    document.body.appendChild(this.modalElement)
    this.modalElement.dataset.portaled = "true"
  }

  restoreModal() {
    if (!this.modalElement || !this.modalHome) return

    this.modalHome.replaceWith(this.modalElement)
    delete this.modalElement.dataset.portaled
    this.modalHome = null
  }

  get modalController() {
    if (!this.modalElement) return null

    return this.application.getControllerForElementAndIdentifier(
      this.modalElement,
      "railsui-modal"
    )
  }
}
